import { Table as ChakraTable, Spinner } from '@chakra-ui/react';
import { UsersRound } from 'lucide-react';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router';

import AvatarListItem from '@app/components/chakra/avatar-list-item';
import Table from '@app/components/chakra/table';
import UserRow from '@app/components/chakra/user-row';
import { useGroupsQuery } from '@app/services/group';
import { Group } from '@app/types/api/group';
import { Avatar } from '@chakra-snippets/avatar';

interface SubgroupTabProps {
  ids: string[];
}

const SubgroupTab = ({ ids }: SubgroupTabProps) => {
  const { data: subgroups, isLoading } = useGroupsQuery({ ids, searchText: '' });
  const navigate = useNavigate();

  const rows = useMemo(() => {
    if (!subgroups?.pages[0].groups) return [];

    return subgroups.pages[0].groups.map((subgroup: Group) => {
      let leadLabel = '';
      const leadCount = subgroup.leads?.length || 0;
      if (leadCount) {
        leadLabel = `${subgroup.leads?.[0]?.profile?.firstName} ${subgroup.leads?.[0]?.profile?.lastName}`;

        if (leadCount > 1) {
          leadLabel += ` +${leadCount - 1}`;
        }
      }

      return (
        <ChakraTable.Row key={subgroup.id}>
          <ChakraTable.Cell>
            <AvatarListItem
              avatar={<Avatar backgroundColor="gray.300" size="md" fallback={<UsersRound />} icon={<UsersRound />} />}
              name={subgroup.name}
              subtitles={[subgroup.description || '']}
              onClick={() => navigate(`/usermgmt/groups/${subgroup.id}`)}
            />
          </ChakraTable.Cell>
          <ChakraTable.Cell>
            {subgroup.leads?.length ? (
              <UserRow
                option={{
                  ...subgroup.leads?.[0],
                  label: leadLabel,
                }}
                size="sm"
              />
            ) : (
              '-'
            )}
          </ChakraTable.Cell>
          <ChakraTable.Cell>
            {subgroup.count}
            {' '}
            Members
          </ChakraTable.Cell>
        </ChakraTable.Row>
      );
    });
  }, [subgroups, navigate]);

  if (isLoading) {
    return <Spinner data-testid="loading-spinner" />;
  }

  return (
    <Table
      type="subgroups"
      rows={rows}
      columns={[
        {
          id: 'name',
          label: 'Name',
        },
        {
          id: 'leads',
          label: 'Leads',
        },
        {
          id: 'total-members',
          label: 'Total Members',
        },
      ]}
      label="Subgroups"
    />
  );
};

export default SubgroupTab;
